import { useMemo, useState } from 'react';
import type { CommandAction, Device } from '@glyph/protocol-ts';
import { UPDATE_LOCK_ENABLED } from '@glyph/protocol-ts';
import {
  fmtAgo,
  fmtDur,
  fmtMs,
  isNewer,
  useAlerts,
  useCan,
  useClient,
  useDevices,
  useEvents,
  useFleetAdmin,
  useLockUpdates,
  useMe,
  useReleases,
} from '@glyph/core';
import {
  BarMeter,
  Button,
  Card,
  CardFooterLink,
  CardHeader,
  CellMono,
  CellText,
  CellTitle,
  Checkbox,
  CloudIcon,
  DataTable,
  DeepgramMark,
  Dialog,
  EmptyState,
  IconButton,
  PageHeader,
  PencilIcon,
  Section,
  SegmentedControl,
  Select,
  StatusPill,
  Tag,
  TextInput,
  severityTone,
  useToast,
} from '@glyph/ui';
import { fmtTs } from './shared';
import { RenameDeviceDialog } from './RenameDeviceDialog';
import { DeepgramKeyCard } from './DeepgramKeyCard';
import { HotspotDefaultCard } from './HotspotPassword';
import { LookDialog } from './LookDialog';
import { ApplyPresetDialog } from './PresetsScreen';
import { GroupsDialog } from './GroupsPanel';
import { AddToScheduleDialog } from './SchedulesScreen';

export interface DevicesScreenProps {
  onOpenDevice: (id: string) => void;
  onGoAlerts: () => void;
  /** Push update: with the units ticked on this page, or none (the Updates page then picks every unit behind stable). */
  onGoUpdates: (deviceIds?: string[]) => void;
  /** Scope to one group: only its units and their alerts (the group's own page). */
  eventId?: string;
  /** Open a group's page from the Groups editor. */
  onOpenGroup?: (id: string) => void;
  title?: string;
}

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'live', label: 'Live' },
  { value: 'standby', label: 'Standby' },
  { value: 'offline', label: 'Offline' },
] as const;

/** Devices (fleet): table with filters + search, Open alerts card, Firmware summary card. */
const EDIT_GROUP_ROLES = new Set(['owner', 'admin', 'platform']);
const SHOW_CLAIM_CODES = false;

export function DevicesScreen({ onOpenDevice, onGoAlerts, onGoUpdates, eventId, onOpenGroup, title = 'Devices' }: DevicesScreenProps) {
  const [filter, setFilter] = useState<string>('all');
  const [groupFilter, setGroupFilter] = useState<string>('');
  const [editGroups, setEditGroups] = useState(false);
  const [q, setQ] = useState('');
  const devices = useDevices();
  const alerts = useAlerts(true);
  const releases = useReleases('stable');
  const lockUpdates = useLockUpdates();
  const toast = useToast();
  const [bulk, setBulk] = useState<null | 'lock' | 'unlock'>(null);
  const canRename = useCan('device.rename');
  const [renaming, setRenaming] = useState<Device | null>(null);
  const me = useMe();
  const canApprove = me.data?.role === 'platform';
  const canEditGroups = EDIT_GROUP_ROLES.has(me.data?.role ?? '');
  // Rollouts (the Updates page) are platform work on the server; per-unit installs are admin+. Operators see neither.
  const canRollout = me.data?.role === 'platform';
  const canAssign = me.data?.role === 'platform';
  const shows = useEvents();
  const admin = useFleetAdmin();
  // Group actions: tick units, then act on all of them at once.
  const client = useClient();
  const [sel, setSel] = useState<string[]>([]);
  const [group, setGroup] = useState<null | 'reboot' | 'shutdown'>(null);
  const [groupBusy, setGroupBusy] = useState(false);
  const canReboot = useCan('system.reboot');
  const canShutdown = useCan('system.shutdown');
  const canLook = useCan('settings.patch');
  const [look, setLook] = useState(false);
  const [presetFor, setPresetFor] = useState(false);
  const [scheduleFor, setScheduleFor] = useState(false);
  const canSchedule = EDIT_GROUP_ROLES.has(me.data?.role ?? ''); // schedules.manage: admin and up
  // Zero-touch units sit here until approved; they are not part of the fleet counts or table.
  const pending = useMemo(() => (eventId ? [] : (devices.data ?? []).filter((d) => d.status === 'pending')), [devices.data, eventId]);
  const [draft, setDraft] = useState<Record<string, { name: string; event_id: string }>>({});
  const [claim, setClaim] = useState<null | { name: string; event_id: string; code?: string; expires_at?: number | null }>(null);
  const all = useMemo(
    () => (devices.data ?? []).filter((d) => d.status !== 'pending' && (!eventId || d.assignment?.event_id === eventId)),
    [devices.data, eventId],
  );
  const lockedCount = UPDATE_LOCK_ENABLED ? all.filter((d) => d.update_locked).length : 0;
  const runBulk = async () => {
    if (!bulk) return;
    const r = await lockUpdates.lock({ all: true, locked: bulk === 'lock' });
    const queued = r.results.filter((x) => x.status === 'queued').length;
    toast.push(
      `${bulk === 'lock' ? 'Locked' : 'Unlocked'} updates on ${r.results.length} unit${r.results.length === 1 ? '' : 's'}${queued ? ` · ${queued} queued until online` : ''}`,
      'ok',
    );
    setBulk(null);
  };
  const stable =
    releases.data?.[0]?.version ??
    [...all.map((d) => d.agent_version)].sort((a, b) => (isNewer(a, b) ? -1 : 1))[0] ??
    '—';
  const list = all.filter(
    (d) =>
      (filter === 'all' || d.state === filter) &&
      (!groupFilter || (groupFilter === '__none' ? !d.assignment?.event_id : d.assignment?.event_id === groupFilter)) &&
      `${d.name} ${d.assignment?.event_name ?? ''} ${d.id}`.toLowerCase().includes(q.toLowerCase()),
  );
  const count = (s: string) => all.filter((d) => d.state === s).length;
  const fw = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of all) m.set(d.agent_version, (m.get(d.agent_version) ?? 0) + 1);
    return [...m.entries()].sort((a, b) => (isNewer(a[0], b[0]) ? -1 : 1));
  }, [all]);
  const open = useMemo(() => {
    const ids = new Set(all.map((d) => d.id));
    return (alerts.data ?? []).filter((a) => !eventId || ids.has(a.device_id));
  }, [alerts.data, all, eventId]);
  const uptime = (d: Device) =>
    d.online ? fmtDur(d.uptime_s) : d.last_seen ? `seen ${fmtAgo(d.last_seen)}` : '—';
  const event = (d: Device) => d.assignment?.event_name ?? 'Unassigned';
  const fwNote = (d: Device) =>
    UPDATE_LOCK_ENABLED && d.update_locked
      ? 'LOCKED'
      : d.agent_version !== stable && isNewer(stable, d.agent_version)
        ? `→ ${stable}`
        : '';
  const bulkActions = !UPDATE_LOCK_ENABLED ? null : (
    <>
      <Button
        size="responsive"
        onClick={() => setBulk('lock')}
        disabled={lockUpdates.isPending || !all.length}
      >
        Lock updates on all
      </Button>
      <Button
        size="responsive"
        onClick={() => setBulk('unlock')}
        disabled={lockUpdates.isPending || !all.length}
      >
        Unlock all
      </Button>
    </>
  );
  const level = (d: Device) => (d.audio_dbfs == null ? 0 : Math.round(((d.audio_dbfs + 60) / 60) * 100));
  const selected = useMemo(() => all.filter((d) => sel.includes(d.id)), [all, sel]);
  const allTicked = list.length > 0 && list.every((d) => sel.includes(d.id));
  const toggleAll = () => setSel(allTicked ? sel.filter((id) => !list.some((d) => d.id === id)) : [...new Set([...sel, ...list.map((d) => d.id)])]);
  const toggleOne = (d: Device, on: boolean) => setSel(on ? [...new Set([...sel, d.id])] : sel.filter((id) => id !== d.id));
  const runGroup = async (action: CommandAction & ('system.identify' | 'system.reboot' | 'system.shutdown'), payload: Record<string, unknown>, label: string) => {
    setGroupBusy(true);
    const targets = selected.filter((d) => d.online);
    const failed: string[] = [];
    for (const d of targets) {
      try {
        await client.cmd(d.id, action, payload as never);
      } catch {
        failed.push(d.name);
      }
    }
    const skipped = selected.length - targets.length;
    toast.push(
      `${label} sent to ${targets.length - failed.length} unit${targets.length - failed.length === 1 ? '' : 's'}${skipped ? ` · ${skipped} offline skipped` : ''}${failed.length ? ` · failed: ${failed.join(', ')}` : ''}`,
      failed.length ? 'warn' : 'ok',
    );
    setGroupBusy(false);
    setGroup(null);
  };
  const assignGroup = async (eventId: string) => {
    if (!eventId) return;
    setGroupBusy(true);
    let n = 0;
    try {
      for (const d of selected) {
        await shows.assign({ deviceId: d.id, eventId: eventId === '__none' ? null : eventId });
        n++;
      }
      const name = shows.data?.find((e) => e.id === eventId)?.name;
      toast.push(eventId === '__none' ? `${n} unit${n === 1 ? '' : 's'} removed from their group` : `${n} unit${n === 1 ? '' : 's'} added to ${name ?? 'the group'}`, 'ok');
    } catch (e) {
      toast.push((e as Error).message, 'warn');
    }
    setGroupBusy(false);
  };
  const groupOptions = [
    { value: '', label: 'Add to group…' },
    ...(shows.data ?? []).map((e) => ({ value: e.id, label: e.name })),
    { value: '__none', label: 'Remove from group' },
  ];
  const groupBar = sel.length > 0 && (
    <div className="flex flex-wrap items-center gap-2 border-b border-line bg-selected px-4 py-2.5" data-testid="group-actions">
      <span className="font-mono text-sm text-fg2 tabular">
        {sel.length} selected
      </span>
      {canApprove && <Select value="" onChange={(v) => void assignGroup(v)} options={groupOptions} aria-label="Add to group" disabled={groupBusy} />}
      <Button size="xs" onClick={() => void runGroup('system.identify', {}, 'Identify')} disabled={groupBusy}>
        Identify
      </Button>
      {canLook && (
        <Button size="xs" onClick={() => setLook(true)} disabled={groupBusy}>
          Look…
        </Button>
      )}
      {canSchedule && (
        <Button size="xs" onClick={() => setScheduleFor(true)} disabled={groupBusy}>
          Schedule…
        </Button>
      )}
      {canLook && (
        <Button size="xs" onClick={() => setPresetFor(true)} disabled={groupBusy}>
          Preset…
        </Button>
      )}
      {canReboot && (
        <Button size="xs" onClick={() => setGroup('reboot')} disabled={groupBusy}>
          Reboot
        </Button>
      )}
      {canShutdown && (
        <Button size="xs" variant="destructive-outline" onClick={() => setGroup('shutdown')} disabled={groupBusy}>
          Shut down
        </Button>
      )}
      {canRollout && (
        <Button size="xs" variant="primary" onClick={() => onGoUpdates(sel)} disabled={groupBusy}>
          Update…
        </Button>
      )}
      <Button size="xs" variant="ghost" className="ml-auto" onClick={() => setSel([])}>
        Clear
      </Button>
    </div>
  );
  const showOptions = [{ value: '', label: 'No group' }, ...(shows.data ?? []).map((e) => ({ value: e.id, label: e.name }))];
  const assignOne = async (d: Device, v: string) => {
    if (v === '__edit') {
      setEditGroups(true);
      return;
    }
    try {
      await shows.assign({ deviceId: d.id, eventId: v || null });
      toast.push(v ? `${d.name} → ${shows.data?.find((e) => e.id === v)?.name ?? 'group'}` : `${d.name} removed from its group`, 'ok');
    } catch (e) {
      toast.push((e as Error).message, 'warn');
    }
  };
  const rowGroupOptions = [{ value: '', label: 'Unassigned' }, ...(shows.data ?? []).map((e) => ({ value: e.id, label: e.name })), ...(canEditGroups ? [{ value: '__edit', label: 'Edit groups…' }] : [])];
  const groupFilterOptions = [{ value: '', label: 'All groups' }, ...(shows.data ?? []).map((e) => ({ value: e.id, label: e.name })), { value: '__none', label: 'Unassigned' }];
  const groupControls = !eventId && (
    <>
      <Select value={groupFilter} onChange={setGroupFilter} options={groupFilterOptions} aria-label="Group" />
      {canEditGroups && (
        <Button size="responsive" onClick={() => setEditGroups(true)}>
          Edit groups
        </Button>
      )}
    </>
  );
  const draftOf = (d: Device) => draft[d.id] ?? { name: d.name, event_id: '' };
  const approve = async (d: Device) => {
    const v = draftOf(d);
    try {
      await admin.approve({ id: d.id, name: v.name.trim() || undefined, event_id: v.event_id || null });
      toast.push(`${v.name.trim() || d.name} approved`, 'ok');
    } catch (e) {
      toast.push((e as Error).message, 'warn');
    }
  };
  const reject = async (d: Device) => {
    try {
      await admin.reject(d.id);
      toast.push(`${d.name} rejected`, 'ok');
    } catch (e) {
      toast.push((e as Error).message, 'warn');
    }
  };
  const mintClaim = async () => {
    if (!claim) return;
    try {
      const r = await admin.claim({ name: claim.name.trim() || undefined, event_id: claim.event_id || null, ttl_minutes: 60 });
      setClaim({ ...claim, code: r.code, expires_at: r.expires_at });
    } catch (e) {
      toast.push((e as Error).message, 'warn');
    }
  };
  const pendingCard = canApprove && pending.length > 0 && (
    <Card>
      <CardHeader title="Waiting for approval" meta={`${pending.length} NEW`} metaTone="accent" />
      {pending.map((d) => {
        const v = draftOf(d);
        return (
          <div key={d.id} className="flex flex-col gap-3 border-t border-line px-4 py-3 md:flex-row md:items-center">
            <div className="flex min-w-0 flex-col gap-0.5 md:w-[260px]">
              <span className="font-mono text-base">{d.serial || d.id}</span>
              <span className="font-mono text-xs text-fg4">
                {d.online ? 'ONLINE' : 'OFFLINE'} · {d.address ?? d.hotspot_ssid ?? '—'} · agent {d.agent_version || '—'} · {d.enrolled_at ? fmtAgo(d.enrolled_at) : ''}
              </span>
            </div>
            <TextInput value={v.name} onChange={(e) => setDraft({ ...draft, [d.id]: { ...v, name: e.target.value } })} placeholder="Name" className="md:w-[200px]" />
            <Select value={v.event_id} onChange={(event_id) => setDraft({ ...draft, [d.id]: { ...v, event_id } })} options={showOptions} aria-label="Show" />
            <div className="flex gap-2 md:ml-auto">
              <Button variant="primary" size="responsive" onClick={() => void approve(d)} disabled={admin.busy}>
                Approve
              </Button>
              <Button size="responsive" onClick={() => void reject(d)} disabled={admin.busy}>
                Reject
              </Button>
            </div>
          </div>
        );
      })}
    </Card>
  );
  // Claim codes are kept in the API for the provisioning station but have no place on the fleet page: units enrol
  // themselves with the fleet token and are approved here. Flip SHOW_CLAIM_CODES to bring the button back.
  const claimBtn = SHOW_CLAIM_CODES && canApprove && !eventId && (
    <Button size="responsive" onClick={() => setClaim({ name: '', event_id: '' })}>
      Claim code
    </Button>
  );

  const alertsCard = (
    <Card>
      <CardHeader title="Open alerts" meta={`${open.length} OPEN`} metaTone="accent" />
      {open.length === 0 && <EmptyState label="Nothing open" />}
      {open.slice(0, 4).map((a) => (
        <div key={a.id} className="flex flex-col gap-1.5 border-t border-line px-4 py-3">
          <div className="flex justify-between gap-2">
            <span className="meta text-fg2">
              {all.find((d) => d.id === a.device_id)?.name ?? 'Platform'} · {fmtTs(a.ts)}
            </span>
            <Tag tone={severityTone(a.severity)}>{a.severity}</Tag>
          </div>
          <span className="text-base font-normal leading-[1.4] text-fg2">{a.detail}</span>
          <Button
            size="mobile"
            variant="ghost"
            mono
            className="self-start md:hidden"
            onClick={() => void alerts.ack(a)}
          >
            CLOSE
          </Button>
        </div>
      ))}
      <CardFooterLink onClick={onGoAlerts}>ALL ALERTS →</CardFooterLink>
    </Card>
  );
  const fwCard = (
    <Card>
      <CardHeader
        title="Firmware"
        meta={`STABLE ${stable}${lockedCount ? ` · ${lockedCount} LOCKED` : ''}`}
        metaTone={lockedCount ? 'warn' : undefined}
      />
      {fw.map(([v, n]) => (
        <div key={v} className="flex justify-between border-t border-line px-4 py-[11px]">
          <span className="font-mono text-base text-fg2">{v}</span>
          <span className="font-mono text-sm text-fg4">
            {n} unit{n === 1 ? '' : 's'}
          </span>
        </div>
      ))}
      {bulkActions && <div className="flex flex-wrap gap-2 border-t border-line px-4 py-3">{bulkActions}</div>}
      {canRollout && <CardFooterLink onClick={onGoUpdates}>PUSH UPDATE →</CardFooterLink>}
    </Card>
  );

  return (
    <Section>
      <GroupsDialog open={editGroups} onClose={() => setEditGroups(false)} onOpenGroup={onOpenGroup} />
      <RenameDeviceDialog open={!!renaming} onClose={() => setRenaming(null)} deviceId={renaming?.id} currentName={renaming?.name ?? ''} serial={renaming?.serial} currentSsid={renaming?.hotspot_ssid} />
      <PageHeader
        desktopOnly
        title={title}
        meta={`${all.length} UNITS · ${count('live')} LIVE · ${count('standby')} STANDBY · ${count('offline')} OFFLINE${groupFilter ? ` · ${list.length} SHOWN` : ''}`}
        actions={
          <>
            <SegmentedControl value={filter} onChange={setFilter} options={FILTERS} />
            {groupControls}
            <TextInput
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search unit or group"
              className="w-[200px]"
            />
            {claimBtn}
          </>
        }
      />
      {pendingCard}
      {/* mobile */}
      <div className="flex flex-col gap-4 md:hidden">
        <SegmentedControl fullWidth value={filter} onChange={setFilter} options={FILTERS} />
        <div className="flex flex-wrap gap-2">{groupControls}</div>
        {claimBtn}
        <Card>
          <CardHeader
            title="Units"
            meta={`${count('live')} LIVE · ${count('standby')} STANDBY · ${count('offline')} OFFLINE`}
          />
          {list.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => onOpenDevice(d.id)}
              className="flex w-full flex-col gap-1.5 border-0 border-t border-line bg-transparent px-4 py-3 text-left text-fg hover:bg-hover"
            >
              <div className="flex w-full items-center justify-between gap-3">
                <span className="text-xl font-medium">{d.name}</span>
                <StatusPill state={d.state}>{d.state}</StatusPill>
              </div>
              <span className="text-md text-fg2">{event(d)}</span>
              <span className="font-mono text-sm leading-[1.4] text-fg4">
                captions {d.asr_model} · cloud {fmtMs(d.cloud_rtt_ms)} · Deepgram {fmtMs(d.dg_rtt_ms)} · up {uptime(d)} · fw {d.agent_version}{' '}
                <span className={d.update_locked ? 'text-warn' : 'text-accent'}>{fwNote(d)}</span>
              </span>
            </button>
          ))}
        </Card>
        {alertsCard}
        {fwCard}
        <DeepgramKeyCard devices={all} />
        <HotspotDefaultCard />
      </div>
      {/* desktop */}
      <div className="hidden flex-wrap items-start gap-5 md:flex">
        <Card className="min-w-0" style={{ flex: '1 1 600px' }}>
          {groupBar}
          <DataTable
            rows={list}
            rowKey={(d) => d.id}
            onRowClick={(d) => onOpenDevice(d.id)}
            minWidth={900}
            empty={<EmptyState label="No units match" />}
            columns={[
              {
                key: 'sel',
                header: <Checkbox checked={allTicked} onChange={toggleAll} label="Select all units" />,
                width: '24px',
                cell: (d) => <Checkbox checked={sel.includes(d.id)} onChange={(v) => toggleOne(d, v)} label={`Select ${d.name}`} />,
              },
              {
                key: 'unit',
                header: 'Unit',
                width: '1.5fr',
                cell: (d) => (
                  <div className="flex min-w-0 items-center gap-2">
                    <CellTitle title={d.name} sub={d.address ?? d.hotspot_ssid ?? (d.assignment?.org_name || '—')} />
                    {canRename && (
                      <IconButton
                        framed={false}
                        label={`Rename ${d.name}`}
                        className="opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          setRenaming(d);
                        }}
                      >
                        <PencilIcon />
                      </IconButton>
                    )}
                  </div>
                ),
              },
              {
                key: 'event',
                header: 'Group',
                width: '1.5fr',
                cell: (d) =>
                  canAssign ? (
                    <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                      <Select size="sm" value={d.assignment?.event_id ?? ''} options={rowGroupOptions} onChange={(v) => void assignOne(d, v)} aria-label={`Group of ${d.name}`} disabled={shows.busy} className="max-w-full" />
                    </div>
                  ) : (
                    <CellText>{event(d)}</CellText>
                  ),
              },
              {
                key: 'state',
                header: 'State',
                width: '.9fr',
                cell: (d) => (
                  <div className="flex flex-col gap-1">
                    <StatusPill state={d.state}>{d.state}</StatusPill>
                    {(d.state === 'downloading' || d.state === 'updating') && (
                      <span className="font-mono text-xs text-fg4">
                        {d.update_target ?? ''}{d.state === 'downloading' && d.update_pct != null ? ` · ${Math.round(d.update_pct)} %` : ''}
                      </span>
                    )}
                  </div>
                ),
              },
              { key: 'asr', header: 'Captions', width: '.8fr', cell: (d) => <CellMono>{d.asr_model}</CellMono> },
              {
                key: 'lat',
                header: 'Latency',
                width: '1fr',
                align: 'center',
                cell: (d) => (
                  <CellMono tabular>
                    {d.online ? (
                      <span className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap">
                        <CloudIcon /> {fmtMs(d.cloud_rtt_ms)}
                        <DeepgramMark className="ml-1" /> {fmtMs(d.dg_rtt_ms)}
                      </span>
                    ) : (
                      '—'
                    )}
                  </CellMono>
                ),
              },
              { key: 'audio', header: 'Audio', width: '1fr', cell: (d) => <BarMeter value={level(d)} /> },
              { key: 'up', header: 'Uptime', width: '.9fr', cell: (d) => <CellMono>{uptime(d)}</CellMono> },
              {
                key: 'fw',
                header: 'Firmware',
                width: '1fr',
                cell: (d) => (
                  <div className="flex flex-col gap-1">
                    <CellMono>{d.agent_version}</CellMono>
                    <span
                      className={`font-mono text-xs font-medium tracking-[.04em] ${d.update_locked ? 'text-warn' : 'text-accent'}`}
                    >
                      {fwNote(d)}
                    </span>
                  </div>
                ),
              },
              {
                key: 'go',
                header: '',
                width: '24px',
                cell: () => <span className="text-lg text-fg5">→</span>,
              },
            ]}
          />
        </Card>
        <div className="flex min-w-[260px] flex-col gap-5" style={{ flex: '0 1 300px' }}>
          {alertsCard}
          {fwCard}
          <DeepgramKeyCard devices={all} />
          <HotspotDefaultCard />
        </div>
      </div>
      <Dialog
        open={!!group}
        onClose={() => setGroup(null)}
        title={group === 'reboot' ? `Reboot ${selected.length} unit${selected.length === 1 ? '' : 's'}?` : `Shut down ${selected.length} unit${selected.length === 1 ? '' : 's'}?`}
        meta={group === 'reboot' ? 'CAPTIONS DROP FOR ~30 S ON EACH · OFFLINE UNITS ARE SKIPPED' : 'EACH OLED TURNS OFF · SOMEONE MUST PULL POWER TO RESTART · OFFLINE UNITS ARE SKIPPED'}
        confirm={{
          label: group === 'reboot' ? 'Reboot' : 'Shut down',
          destructive: true,
          busy: groupBusy,
          onClick: () => void (group === 'reboot' ? runGroup('system.reboot', { confirm: true }, 'Reboot') : runGroup('system.shutdown', {}, 'Shut down')),
        }}
      >
        {selected.map((d) => d.name).join(', ')}
      </Dialog>
      <LookDialog open={look} onClose={() => setLook(false)} devices={all} targets={selected} />
      <ApplyPresetDialog open={presetFor} onClose={() => setPresetFor(false)} targets={sel} />
      <AddToScheduleDialog open={scheduleFor} onClose={() => setScheduleFor(false)} targets={selected} />
      <Dialog
        open={!!claim}
        onClose={() => setClaim(null)}
        title={claim?.code ? 'Claim code' : 'New claim code'}
        cancelLabel={claim?.code ? 'Done' : 'Cancel'}
        confirm={claim?.code ? undefined : { label: 'Create code', onClick: mintClaim, busy: admin.busy }}
      >
        {claim && !claim.code && (
          <div className="flex flex-col gap-4">
            <label className="flex flex-col gap-2">
              <span className="micro text-fg3">Unit name</span>
              <TextInput value={claim.name} onChange={(e) => setClaim({ ...claim, name: e.target.value })} block autoFocus />
            </label>
            <label className="flex flex-col gap-2">
              <span className="micro text-fg3">Show</span>
              <Select value={claim.event_id} onChange={(event_id) => setClaim({ ...claim, event_id })} options={showOptions} block />
            </label>
          </div>
        )}
        {claim?.code && (
          <div className="flex flex-col gap-3">
            <span className="font-mono text-3xl tracking-[.12em]">{claim.code}</span>
            <span className="font-mono text-xs text-fg4">TYPE IT ON THE UNIT'S CLOUD PAGE · SINGLE USE · VALID 60 MIN</span>
          </div>
        )}
      </Dialog>
      <Dialog
        open={!!bulk}
        onClose={() => setBulk(null)}
        title={
          bulk === 'lock'
            ? `Lock updates on all ${all.length} units?`
            : `Unlock updates on all ${all.length} units?`
        }
        meta={
          bulk === 'lock'
            ? 'NO UNIT INSTALLS ANYTHING UNTIL UNLOCKED'
            : 'ROLLOUTS AND LOCAL INSTALLS BECOME POSSIBLE AGAIN'
        }
        confirm={{
          label: bulk === 'lock' ? 'Lock all' : 'Unlock all',
          destructive: bulk === 'unlock',
          onClick: runBulk,
        }}
      >
        {bulk === 'lock'
          ? 'Every unit refuses cloud rollouts, "Install staged", "Install from file" and OS images. Roll back and the automatic health-check rollback stay available. Offline units receive the lock when they reconnect.'
          : 'Rollouts may offer releases to these units and operators may install staged bundles locally. Offline units receive the change when they reconnect.'}
      </Dialog>
    </Section>
  );
}
